// src/utilities/formattedDisplayComponents/FormConfigValidator.ts
import Ajv, { ErrorObject } from "ajv";
import formConfigSchema from "../../components/components/formFillComponents/schemas/form-config-schema.json";

// This interface defines the structure of our validation result.
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// A helper function to add errors with a clear path.
const addError = (errors: string[], path: string, message: string) => {
  errors.push(`Error at ${path}: ${message}`);
};

// Validator for a 'showIf' condition object.
const validateCondition = (condition: any, path: string, errors: string[]) => {
  if (typeof condition !== 'object' || condition === null) {
    addError(errors, path, 'must be an object.');
    return;
  }
  if (typeof condition.field !== 'string') addError(errors, `${path}.field`, 'is missing or not a string.');
  if (typeof condition.operator !== 'string') addError(errors, `${path}.operator`, 'is missing or not a string.');
  if (typeof condition.value !== 'string' && !Array.isArray(condition.value)) {
    addError(errors, `${path}.value`, 'must be a string or an array of strings.');
  }
};

// Validator for a 'question' object.
const validateQuestion = (question: any, path: string, errors: string[]) => {
  if (typeof question !== 'object' || question === null) {
    addError(errors, path, 'must be an object.');
    return;
  }
  // Check required fields
  if (typeof question.id !== 'string') addError(errors, `${path}.id`, 'is missing or not a string.');
  if (typeof question.label !== 'string') addError(errors, `${path}.label`, 'is missing or not a string.');
  if (typeof question.type !== 'string') addError(errors, `${path}.type`, 'is missing or not a string.');

  // Check optional fields
  if (question.options && !Array.isArray(question.options)) {
    addError(errors, `${path}.options`, 'must be an array.');
  }
  if (question.showIf) {
    if (!Array.isArray(question.showIf)) {
      addError(errors, `${path}.showIf`, 'must be an array.');
    } else {
      question.showIf.forEach((cond: any, i: number) => validateCondition(cond, `${path}.showIf[${i}]`, errors));
    }
  }
};

// Validator for a 'step' object.
const validateStep = (step: any, path: string, errors: string[]) => {
  if (typeof step !== 'object' || step === null) {
    addError(errors, path, 'must be an object.');
    return;
  }
  if (typeof step.id !== 'string') addError(errors, `${path}.id`, 'is missing or not a string.');
  if (typeof step.label !== 'string') addError(errors, `${path}.label`, 'is missing or not a string.');
  if (!Array.isArray(step.questions)) {
    addError(errors, `${path}.questions`, 'is missing or not an array.');
  } else {
    step.questions.forEach((q: any, i: number) => validateQuestion(q, `${path}.questions[${i}]`, errors));
  }
};

/**
 * Main validation function for the entire Form Configuration object.
 * It checks the structure against the expected schema.
 * @param config The parsed JSON object for the form config (unwrapped).
 * @returns A ValidationResult object with a boolean and a list of error strings.
 */
export const validateFormConfig = (config: any): ValidationResult => {
  const errors: string[] = [];

  if (typeof config !== 'object' || config === null) {
    return { isValid: false, errors: ['The root configuration must be an object.'] };
  }

  // The 'config' object is the unwrapped form configuration.
  if (typeof config.formId !== 'string' || !config.formId) {
    addError(errors, 'formId', 'is missing or not a string.');
  }

  if (!Array.isArray(config.steps)) {
    addError(errors, 'steps', 'is missing or not an array.');
  } else {
    config.steps.forEach((step: any, i: number) => validateStep(step, `steps[${i}]`, errors));
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};